import { useRef } from 'react'

const noFirstRenderRun = false
let checkRenders = true
if (process.env.NODE_ENV === 'production') {
  checkRenders = false
}

const hasKeys = arg => {
  return (
    typeof arg === 'object' &&
    !Array.isArray(arg) &&
    arg !== null
  )
}

/**
 * @function
 * @description This prints out what causes a render in a react component. We need this function in order to squeeze out renders which slows down scanning.
 * @param {object} a Object 1 to compare.
 * @param {object} b Object 2 to compare.
 * @returns {boolean} Indicates whether a change was found.
 */
export const shallowCompare = (a, b) => {
  let result = true
  for (const key1 in a) {
    if (!(key1 in b)) {
      // eslint-disable-next-line no-console
      console.log(`key=${key1} is in a but not b.`)
      result = false
    }
    if (a[key1] !== b[key1]) {
      // eslint-disable-next-line no-console
      console.log(`objects differ at key=${key1}, ${JSON.stringify(a[key1])}, ${JSON.stringify(b[key1])}`)
      result = false
    }
  }
  for (const key2 in b) {
    if (!(key2 in a)) {
      // eslint-disable-next-line no-console
      console.log(`key= ${key2} is in b but not a.`)
      result = false
    }
  }
  return result
}

export class ExplainRenders {
  constructor(props, state, reduxSelectorOutput) {
    if (noFirstRenderRun) {
      this.prevProps = props
      this.prevState = state
      this.prevReduxSelectorOutput = reduxSelectorOutput
    } else {
      this.prevProps = {}
      this.prevState = {}
      this.prevReduxSelectorOutput = {}
    }
  }

  snapshot(location, props, state, reduxSelectorOutput) {
    // eslint-disable-next-line no-console
    console.log('---------------------------------------------------------------------')
    // eslint-disable-next-line no-console
    console.log(`%c ${location}`, 'font-weight:bold; font-size:16px;')
    // eslint-disable-next-line no-console
    console.log('-- Props')
    shallowCompare(this.prevProps, props)
    if (state !== undefined) {
      // eslint-disable-next-line no-console
      console.log('-- State')
      shallowCompare(this.prevState, state)
    }
    if (reduxSelectorOutput !== undefined) {
      // eslint-disable-next-line no-console
      console.log('-- reduxSelectorOutput')
      if (hasKeys(reduxSelectorOutput)) {
        shallowCompare(this.prevReduxSelectorOutput, reduxSelectorOutput)
      } else {
        if (this.prevReduxSelectorOutput !== reduxSelectorOutput) {
          // eslint-disable-next-line no-console
          console.log(`differ ${JSON.stringify(this.prevReduxSelectorOutput)}, ${JSON.stringify(reduxSelectorOutput)}`)
        }
      }
    }
    // eslint-disable-next-line no-console
    console.log('---------------------------------------------------------------------')
    this.prevProps = props
    this.prevState = state
    this.prevReduxSelectorOutput = reduxSelectorOutput
  }
}

/**
 * @function
 * @description Prints the reasons why a component renders. This can be because props change or state changes.
 * @param {string} location A string to identify which component is being traced.
 * @param {object} props The props of the component.
 * @param {object} state The state object of the component. Note: it must be a state object like with userMergeState.
 * @param {object} reduxSelectorOutput The output of redux useSelector in the component. Note: if the function passed
 * to useSelector returns an object, you must use shallowCompare from react-redux as the second argument to useSelector.
 */
const useExplainRenders = (location, props, state, reduxSelectorOutput) => {
  const explainRenders = useRef(new ExplainRenders(props, state, reduxSelectorOutput))
  const firstTime = useRef(true)

  if (noFirstRenderRun && firstTime.current) {
    firstTime.current = false
    return
  }

  if (checkRenders) {
    explainRenders.current.snapshot(location, props, state, reduxSelectorOutput)
  }
}

export default useExplainRenders